import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Select,
  FormErrorMessage,
  VStack,
  Box,
  Text,
  Image,
  useToast,
} from "@chakra-ui/react";
import axiosInstance from "../../helpers/axiosInstance";

export default function AddRVsItemModal({
  isOpen,
  onClose,
  tripId,
  rvs,
  setRVs,
  city,
}) {
  const toast = useToast();
  const [services, setServices] = useState([]);
  const [serviceId, setServiceId] = useState("");
  const [itemData, setItemData] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!isOpen) return;
    setErrors({});
    setServiceId("");
    setItemData(null);

    (async () => {
      try {
        const res = await axiosInstance.get(
          `/bucket-list/filter-third-party-services?category=transportation&preference=rv`
        );
        const raw = res.data.data?.services || [];
        setServices(raw);
      } catch (err) {
        console.error("Error fetching RVs:", err);
      }
    })();
  }, [isOpen]);

  const validate = () => {
    const e = {};
    if (!serviceId) e.serviceId = "RV selection is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    const selected = services.find((s) => s._id === serviceId);
    const payload = {
      service_id: serviceId,
      item_data: selected.data,
      type: "rv",
      city,
    };

    try {
      const res = await axiosInstance.post(
        `/bucket-list/${tripId}/add-service-item`,
        payload
      );
      toast({
        title: "RV added",
        description: res.data.message,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      if (res.data.success) {
        const newRV = { ...selected.data, id: selected.data.id || selected.data.booking_url };
        const newList = rvs.some((r) => r.id === newRV.id)
          ? rvs // already there → keep as-is
          : [...rvs, newRV]; // else append

        setRVs(newList);
        setTimeout(onClose, 100); // Give React time to update parent state
      }
    } catch (err) {
      console.error("Error adding RV item:", err);
      toast({
        title: "Error",
        description: "Could not add RV.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent maxH="80vh" overflowY="auto">
        <ModalHeader>Add RV from Bucket List</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl isInvalid={!!errors.serviceId}>
              <FormLabel>RV</FormLabel>
              <Select
                placeholder="Select RV"
                value={serviceId}
                onChange={(e) => {
                  const id = e.target.value;
                  setServiceId(id);
                  const sel = services.find((s) => s._id === id);
                  if (sel) setItemData(sel.data);
                }}
              >
                {services.map((s) => (
                  <option key={s._id} value={s._id}>
                    {s.data.vehicle_name}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>{errors.serviceId}</FormErrorMessage>
            </FormControl>

            {itemData && (
              <Box
                borderWidth="1px"
                borderRadius="md"
                p={4}
                mt={2}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box flex="1" pr={4}>
                  <Text fontSize="lg" fontWeight="bold" noOfLines={2} mb={1}>
                    {itemData.vehicle_name}
                  </Text>
                  <Text fontSize="sm" mb={1}>
                    USD {itemData.price_per_day} per day
                    {!itemData.rating && " - No Rating"}
                  </Text>
                  {itemData.rating > 0 ? (
                    <Text fontSize="sm">
                      {itemData.rating} ★ ({itemData.reviews} reviews)
                    </Text>
                  ) : (
                    <Text fontSize="sm">(No reviews available)</Text>
                  )}
                </Box>
                <Image
                  src={itemData.image_url}
                  boxSize="100px"
                  objectFit="cover"
                  borderRadius="md"
                  alt={itemData.vehicle_name}
                />
              </Box>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose} mr={3} variant="ghost">
            Cancel
          </Button>
          <Button colorScheme="customGreen" onClick={handleSave}>
            Add RV
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
